'use client'

import { Box, CircularProgress } from '@mui/material'
import { useCueFontSizeOptimizer } from 'app/_hooks'
import Hls from 'hls.js'
import type { Route } from 'next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isIOS, isSafari } from 'react-device-detect'
import { createHls } from '~/libs/hls'
import { Controller } from './Controller'
import { FlashIcon } from './FlashIcon'
import { Subtitle } from './Subtitle'
import {
  FontSizeTierContext,
  readFontSizeTier,
  SizeTier,
  writeFontSizeTier,
} from './useFontSizeTier'
import { FullscreenContext } from './useFullscreen'
import { LangContext } from './useLang'
import { DefaultLogger, LoggerContext, LogType } from './useLogger'
import { VideoScaleProvider } from './useVideoScale'
import { VolumeProvider } from './useVolume'

type Props = {
  videoId: string
  title: string
  src: string | undefined
  duration: number
  contentDuration?: number
  vtts: {
    default: boolean
    url: string
    locale: string
    label: string
  }[]
  hlsParameter: string
  loggers: {
    [key in LogType]?: () => void
  }
  viewingTime?: {
    minutes: number
    seconds: number
    update: (
      currentTime: number,
      metadata: {
        volume: number
        speed: number
        subtitle: string
        quality: string
        fontSize: string
        fullscreen: boolean
      },
    ) => void
  }
  isTheater?: boolean
  onPlay?: () => void
  onContentEnd?: () => void
  onBack?: () => void
  detailHref?: Route
}

export const PlayerBase = ({
  videoId,
  title,
  src,
  duration,
  contentDuration,
  vtts,
  hlsParameter,
  loggers,
  viewingTime,
  isTheater = false,
  onPlay,
  onContentEnd,
  onBack,
  detailHref,
}: Props) => {
  const normalizedLoggers = useMemo(() => ({ ...DefaultLogger, ...loggers }), [loggers])
  const baseRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLVideoElement>(null)
  const [hls, setHls] = useState<Hls | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0.0)
  const [fullscreen, setFullscreen] = useState(false)
  const [lang, setLang] = useState<string | null>(null)
  const [sizeTier, setSizeTier] = useState(readFontSizeTier)
  const [hover, setHover] = useState(true)

  const [isIdle, setIsIdle] = useState(false)
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null)

  const updateSizeTier = useCallback((tier: SizeTier) => {
    writeFontSizeTier(tier)
    setSizeTier(tier)
  }, [])

  useCueFontSizeOptimizer(sizeTier)

  const showedControls = useMemo(() => {
    if (!isPlaying) return true

    return hover && !isIdle
  }, [isIdle, isPlaying, hover])

  useEffect(() => {
    const handleMouseMove = () => {
      setIsIdle(false)

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)

      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true)
      }, 3000)
    }

    handleMouseMove()

    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    }
  }, [])

  useEffect(() => {
    const videoElem = ref.current
    if (videoElem == null || !src) return

    if (Hls.isSupported() && !isSafari && !isIOS) {
      const hls = createHls({ player: 'base', videoId })
      hls.config.xhrSetup = (xhr, url) => {
        xhr.open('GET', `${url}?${hlsParameter}`, true)
      }
      hls.detachMedia()
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src)
      })
      hls.attachMedia(videoElem)
      setHls(hls)

      return () => {
        hls.destroy()
      }
    }
  }, [src, hlsParameter, videoId])

  const logged = useRef({
    watch1Sec: false,
    watch3Sec: false,
    watch5Sec: false,
    watch10Sec: false,
    watch15Sec: false,
    watch30Sec: false,
    watchComplete: false,
  })

  const hlsRef = useRef<Hls | null>(null)
  useEffect(() => {
    hlsRef.current = hls
  }, [hls])

  const metadataRef = useRef({
    subtitle: 'off',
    fontSize: sizeTier,
    fullscreen,
  })
  useEffect(() => {
    metadataRef.current = {
      subtitle: lang || 'off',
      fontSize: sizeTier,
      fullscreen,
    }
  }, [lang, sizeTier, fullscreen])

  const handleLoaded = useCallback(() => {
    const video = ref.current
    if (!video) return

    if (viewingTime) {
      video.currentTime = viewingTime.minutes * 60 + viewingTime.seconds
    } else {
      video.currentTime = 0
    }

    video.play()

    const handleVideoTimeUpdate = () => {
      if (video.currentTime >= 1 && !logged.current.watch1Sec) {
        normalizedLoggers.watch1Sec()
        logged.current.watch1Sec = true
      }
      if (video.currentTime >= 3 && !logged.current.watch3Sec) {
        normalizedLoggers.watch3Sec()
        logged.current.watch3Sec = true
      }
      if (video.currentTime >= 5 && !logged.current.watch5Sec) {
        normalizedLoggers.watch5Sec()
        logged.current.watch5Sec = true
      }
      if (video.currentTime >= 10 && !logged.current.watch10Sec) {
        normalizedLoggers.watch10Sec()
        logged.current.watch10Sec = true
      }
      if (video.currentTime >= 15 && !logged.current.watch15Sec) {
        normalizedLoggers.watch15Sec()
        logged.current.watch15Sec = true
      }
      if (video.currentTime >= 30 && !logged.current.watch30Sec) {
        normalizedLoggers.watch30Sec()
        logged.current.watch30Sec = true
      }
      if (video.currentTime >= video.duration * 0.9 && !logged.current.watchComplete) {
        normalizedLoggers.watchComplete()
        logged.current.watchComplete = true
      }
      if (contentDuration && onContentEnd && video.currentTime >= contentDuration) {
        onContentEnd()
      }

      if (viewingTime) {
        viewingTime.update(video.currentTime, {
          volume: video.muted ? 0 : Math.round(video.volume * 100),
          speed: video.playbackRate,
          subtitle: metadataRef.current.subtitle,
          quality:
            hlsRef.current &&
            hlsRef.current.currentLevel >= 0 &&
            hlsRef.current.levels[hlsRef.current.currentLevel]?.height
              ? `${hlsRef.current.levels[hlsRef.current.currentLevel].height.toString()}p`
              : 'auto',
          fontSize: metadataRef.current.fontSize,
          fullscreen: metadataRef.current.fullscreen,
        })
      }
    }
    video.addEventListener('timeupdate', handleVideoTimeUpdate)

    const handleEndFullscreen = () => {
      setFullscreen(false)
      normalizedLoggers.exitFullscreen()
    }
    video.addEventListener('webkitendfullscreen', handleEndFullscreen)

    let animationFrameId: number

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime)
      animationFrameId = requestAnimationFrame(handleTimeUpdate)
    }

    animationFrameId = requestAnimationFrame(handleTimeUpdate)
    return () => {
      cancelAnimationFrame(animationFrameId)
      video.removeEventListener('webkitendfullscreen', handleEndFullscreen)
      video.removeEventListener('timeupdate', handleVideoTimeUpdate)
    }
  }, [normalizedLoggers, viewingTime, contentDuration, onContentEnd])

  const handleChangeFullscreen = useCallback(
    (value: boolean) => {
      if (value) {
        if (isIOS) {
          const video = ref.current as HTMLVideoElement & { webkitEnterFullscreen?: () => void }
          video.webkitEnterFullscreen?.()
          video.play()
        } else {
          baseRef?.current
            ?.requestFullscreen()
            .then(() => {
              ref.current?.play()
            })
            .catch((err) => {
              console.error('Error attempting to enable full-screen mode:', err)
            })
        }
        setFullscreen(true)
        normalizedLoggers.fullscreen()
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen()
        }
        setFullscreen(false)
        normalizedLoggers.exitFullscreen()
      }
    },
    [baseRef, ref, normalizedLoggers],
  )

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen =
        document.fullscreenElement === baseRef.current ||
        (isIOS && (document as any).webkitFullscreenElement === baseRef.current)
      setFullscreen(isNowFullscreen)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const [flashIconStr, setFlashIconStr] = useState<string>('')
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const flashIcon = useCallback((name: string) => {
    setFlashIconStr(name)
    setVisible(false)

    requestAnimationFrame(() => {
      setVisible(true)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setVisible(false), 500)
    })
  }, [])

  return (
    <FontSizeTierContext.Provider value={{ sizeTier, updateSizeTier }}>
      <LoggerContext.Provider value={normalizedLoggers}>
        <FullscreenContext.Provider value={{ fullscreen, setFullscreen: handleChangeFullscreen }}>
          <LangContext.Provider value={{ lang, setLang }}>
            <VolumeProvider>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                ref={baseRef}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#000',
                    aspectRatio: fullscreen ? undefined : '16 / 9',
                    maxWidth: fullscreen ? '100%' : '100%',
                    maxHeight: fullscreen ? '100%' : '100%',
                  }}
                >
                  {src ? (
                    <video
                      ref={ref}
                      style={{ width: '100%', height: '100%' }}
                      autoPlay
                      playsInline
                      onPlay={() => {
                        setIsPlaying(true)
                        onPlay?.()
                      }}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => {
                        setIsPlaying(false)
                      }}
                      onLoadedMetadata={handleLoaded}
                    >
                      <source src={src} type='application/vnd.apple.mpegurl' />
                      {vtts.map((vtt) => (
                        <track
                          key={vtt.locale}
                          src={`/proxy_resource${new URL(vtt.url).pathname}`}
                          kind='captions'
                          srcLang={vtt.locale}
                          label={vtt.label}
                        />
                      ))}
                    </video>
                  ) : (
                    <CircularProgress color='primary' />
                  )}
                </Box>
                {ref.current && baseRef.current && (
                  <VideoScaleProvider video={ref.current}>
                    <Subtitle video={ref.current} showedControls={showedControls} />
                    <Controller
                      videoId={videoId}
                      title={title}
                      video={ref.current}
                      hls={hls}
                      showedControls={showedControls}
                      isPlaying={isPlaying}
                      currentTime={currentTime}
                      duration={duration}
                      vtts={vtts}
                      flashIcon={flashIcon}
                      isTheater={isTheater}
                      onBack={onBack}
                      detailHref={detailHref}
                    />
                    <FlashIcon icon={flashIconStr} visible={visible} />
                  </VideoScaleProvider>
                )}
              </Box>
            </VolumeProvider>
          </LangContext.Provider>
        </FullscreenContext.Provider>
      </LoggerContext.Provider>
    </FontSizeTierContext.Provider>
  )
}
